import { useQuery } from '@tanstack/react-query';

import { t } from '../../../lib/i18n';
import { fetchWonCars } from '../api';
import { type User, type WonCarWithDetails } from '../types';
import { formatAmount } from '../utils';

/** Sum of the bid and every cost line — the same total the finances tab uses. */
function carTotal(wonCar: WonCarWithDetails): number {
  return (
    (wonCar.bid?.bid || 0) +
    (wonCar.auctionCommission || 0) +
    (wonCar.delivery || 0) +
    (wonCar.certificate || 0) +
    (wonCar.terminal || 0) +
    (wonCar.customsClearance || 0) +
    (wonCar.repair || 0) +
    (wonCar.sale || 0)
  );
}

function carName(wonCar: WonCarWithDetails): string {
  const car = wonCar.car;
  if (!car) return '—';
  const brand = car.ai_brand || car.brand || car.make || '';
  const model = car.ai_model || car.model || '';
  return [brand, model].filter(Boolean).join(' ') || car.listing_id || '—';
}

/**
 * «Виграні» tab — the client's won lots with what each one has cost so far.
 *
 * The artboard draws an empty state here (its mock client had no wins). The
 * empty state is kept for that case, but when there are wins the API serves
 * enough to show them properly — including a thumbnail, which the bids tab
 * does not get.
 */
export function ClientWonTab({ user }: { user: User }) {
  const query = useQuery({ queryKey: ['won-cars'], queryFn: fetchWonCars });

  const wonCars = (query.data ?? []).filter((wonCar) => wonCar.userId?.toString() === user._id);

  if (query.isLoading) {
    return <Empty title={t('common.loading.data')} />;
  }
  if (query.isError) {
    return <Empty title={t('clients.detail.wonTab.failed')} />;
  }
  if (wonCars.length === 0) {
    return <Empty title={t('clients.detail.wonTab.empty')} hint={t('clients.detail.wonTab.emptyHint')} />;
  }

  return (
    <section className="flex flex-col gap-2 lg:block lg:gap-0 lg:overflow-hidden lg:rounded-cf-lg lg:border lg:border-cf-line lg:bg-cf-surface">
      {wonCars.map((wonCar) => (
        <div
          key={wonCar._id}
          className="flex items-center gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface px-3 py-2.5 lg:gap-3.5 lg:rounded-none lg:border-0 lg:border-b lg:border-cf-line-soft lg:px-[18px] lg:py-3"
        >
          {wonCar.car?.thumbnail_image ? (
            <img
              src={wonCar.car.thumbnail_image}
              alt=""
              className="h-10 w-14 shrink-0 rounded-cf-sm bg-cf-fill object-cover lg:h-11 lg:w-16"
            />
          ) : (
            <span className="h-10 w-14 shrink-0 rounded-cf-sm bg-cf-fill lg:h-11 lg:w-16" />
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold lg:text-sm">{carName(wonCar)}</div>
            <div className="truncate text-[11px] text-cf-ink-subtle lg:text-xs">
              {wonCar.car?.listing_id ?? '—'}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[13px] font-medium lg:text-[15px]">
              CHF {formatAmount(carTotal(wonCar))}
            </div>
            <div className="font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle uppercase">
              {t('clients.detail.wonTab.total')}
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}

function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center lg:px-6 lg:py-10">
      {/*
        The artboard's empty truck. It was missing from the admin SPA's static
        root when this screen was written; it is there now, at the same path the
        artboard loads it from.
      */}
      <img
        src="/assets/cf/illu/truck_empty.webp"
        alt=""
        className="mx-auto mb-2.5 h-[90px] w-auto lg:mb-3 lg:h-[110px]"
      />
      <p className="m-0 text-base font-semibold lg:text-[18px]">{title}</p>
      {hint && <p className="mt-1.5 mb-0 text-[13px] text-cf-ink-subtle">{hint}</p>}
    </section>
  );
}
